import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuHistory, LuHandCoins, LuRefreshCw } from "react-icons/lu";
import AppModal from "../common/modal/AppModal";
import AIImageGenerateTab, { CUSTOM_STYLE_ID } from "./AIImageGenerateTab";
import LibraryImageTab from "./LibraryImageTab";
import UploadImageTab from "./UploadImageTab";
import HistoryTab from "./HistoryTab";
import SceneVisualGenerateTab from "./SceneVisualGenerateTab";
import SceneAudioService from "../../services/sceneAudioService";
import InsufficientBalanceModal from "../common/modal/InsufficientBalanceModal";
import { CLOSED_CREDIT_REFUSAL, creditRefusalState } from "../../utils/insufficientBalance";
import i18n from "i18next";
import getMediaBoxSize from "../../utils/getMediaBoxSize";
import { spinForSceneAdd } from "../../utils/sceneAddSpinner";
import { recordPopupResult } from "../ai-chat/aiPopupResults";
import { FPS } from "../../../common/sceneTimeline.es6";
import "./_addImageModal.scss";

const TABS = [
  { id: "ai", label: "AIGenerate", icon: "✨", count: 2 },
  { id: "scene3d", label: "SceneVisual.ThreeD.Tab", icon: "🧊", count: 1 },
  { id: "sceneMotion", label: "SceneVisual.Motion.Tab", icon: "🎞️", count: 1 },
  {
    id: "library",
    label: "ChooseFromLibrary",
    icon: "🖼️",
    count: 1,
  },
  { id: "upload", label: "UploadMyImage", icon: "⬆️", count: 2 },
];

/**
 * Tabs that generate something and therefore share the footer's Oluştur
 * button, its credit estimate and its "Vazgeç returns to the form" behaviour.
 * The library / upload / history tabs have no generate step and no cost.
 */
const GENERATIVE_TABS = new Set(["ai", "scene3d", "sceneMotion"]);

/** Which backend kind each generative tab asks for. */
const SCENE_VISUAL_KIND_BY_TAB = { scene3d: "3d", sceneMotion: "motion" };

const AddImageModal = ({
  show,
  onClose,
  onGenerate,
  onCancel,
  narrationText = "",
  trainingId = null,
  navId = null,
  sceneIndex = null,
  onServerModal,
  onBoxAdded,
  boxes,
  navItems,
  navItemSelected,
  containedViews,
  containedViewSelected,
  boxSelected,
  pluginToolbars,
  uploadFunction,
  onImageSelected,
  titleLabel,
  // Applied by the parent with the editor's own addBox — the two scene-visual
  // tabs hand back a ready box, not a url. Absent (e.g. when the modal is
  // opened as an image picker via onImageSelected) the two tabs still work and
  // simply have nowhere to drop their result, so they are hidden instead.
  onSceneVisualGenerated,
}) => {
  const [activeTab, setActiveTab] = useState("ai");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [model, setModel] = useState("grok");
  const [selectedStyle, setSelectedStyle] = useState("photo");
  const [textInImage, setTextInImage] = useState(false);
  const [promptMode, setPromptMode] = useState("write");
  const [narration, setNarration] = useState("");
  const [fetchedNarrationText, setFetchedNarrationText] = useState("");
  const [customStyleDesc, setCustomStyleDesc] = useState("");
  const [modalKey, setModalKey] = useState(0);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiHasResult, setAiHasResult] = useState(false);
  const [aiNarrationGenerating, setAiNarrationGenerating] = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(CLOSED_CREDIT_REFUSAL);
  const isCustomStyle = selectedStyle === CUSTOM_STYLE_ID;

  const handleInsufficientBalance = useCallback(
    (errData) => {
      setInsufficientBalance(creditRefusalState(errData));
      onClose?.();
    },
    [onClose]
  );

  // Three tabs generate, and the footer drives whichever one is open. Each
  // registers its handlers under its own id rather than into one slot: the AI
  // tab stays mounted while hidden (so its prompt and results survive a trip
  // to another tab), so a single slot would hand the footer the hidden tab's
  // generate function.
  //
  // The registering callbacks below MUST stay stable. The tabs re-register in
  // an effect keyed on the callback identity, so a callback rebuilt on every
  // render would loop: register → setState → render → new identity → register.
  const [generateHandlers, setGenerateHandlers] = useState({});
  const registerGenerate = useCallback(
    (tab, handler) => setGenerateHandlers((prev) => ({ ...prev, [tab]: handler })),
    []
  );
  const handleGenerateReady = useCallback(
    (handler) => registerGenerate("ai", handler),
    [registerGenerate]
  );
  const handle3dGenerateReady = useCallback(
    (handler) => registerGenerate("scene3d", handler),
    [registerGenerate]
  );
  const handleMotionGenerateReady = useCallback(
    (handler) => registerGenerate("sceneMotion", handler),
    [registerGenerate]
  );
  const generateHandler = generateHandlers[activeTab] || null;

  // Credit check state — stores the fn registered by the active tab and the last result
  const [creditCheckHandlers, setCreditCheckHandlers] = useState({});
  const [creditInfo, setCreditInfo] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditPopoverOpen, setCreditPopoverOpen] = useState(false);
  const creditBtnRef = useRef(null);

  const registerCreditCheck = useCallback(
    (tab, handler) => setCreditCheckHandlers((prev) => ({ ...prev, [tab]: handler })),
    []
  );
  const handleCreditCheckReady = useCallback(
    (handler) => registerCreditCheck("ai", handler),
    [registerCreditCheck]
  );
  const handle3dCreditCheckReady = useCallback(
    (handler) => registerCreditCheck("scene3d", handler),
    [registerCreditCheck]
  );
  const handleMotionCreditCheckReady = useCallback(
    (handler) => registerCreditCheck("sceneMotion", handler),
    [registerCreditCheck]
  );
  const creditCheckHandler = creditCheckHandlers[activeTab] || null;

  // A cost shown for one tab is not the cost of another; switching tabs with
  // the popover closed must not leave the old figure to be reopened.
  useEffect(() => {
    setCreditInfo(null);
    setCreditPopoverOpen(false);
  }, [activeTab]);

  const handleCreditCheck = useCallback(async () => {
    if (!creditCheckHandler) return;
    if (creditPopoverOpen) {
      setCreditPopoverOpen(false);
      return;
    }
    setCreditLoading(true);
    setCreditInfo(null);
    try {
      const result = await creditCheckHandler();
      setCreditInfo(result);
      setCreditPopoverOpen(true);
    } catch {
      // silently ignore — the endpoint may not exist yet in local dev
    } finally {
      setCreditLoading(false);
    }
  }, [creditCheckHandler, creditPopoverOpen]);

  // Close popover when clicking outside the button area
  useEffect(() => {
    if (!creditPopoverOpen) return;
    const onClickOutside = (e) => {
      if (creditBtnRef.current && !creditBtnRef.current.contains(e.target)) {
        setCreditPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [creditPopoverOpen]);

  // Her üretken sekme kendi "form'a dön" handler'ını burada register eder.
  // Vazgeç sonuç görünümündeyken bunu çağırıyor; modal kapanmıyor, form state'i
  // (prompt, stil, provider seçimi vb.) korunuyor.
  const [resetResultsHandlers, setResetResultsHandlers] = useState({});
  const registerReset = useCallback(
    (tab, handler) => setResetResultsHandlers((prev) => ({ ...prev, [tab]: handler })),
    []
  );
  const handleResetReady = useCallback((handler) => registerReset("ai", handler), [registerReset]);
  const handle3dResetReady = useCallback(
    (handler) => registerReset("scene3d", handler),
    [registerReset]
  );
  const handleMotionResetReady = useCallback(
    (handler) => registerReset("sceneMotion", handler),
    [registerReset]
  );
  const resetAiResultsHandler = resetResultsHandlers[activeTab] || null;

  const handleAiStateChange = useCallback((state) => {
    setAiGenerating(state.isGenerating);
    setAiHasResult(state.hasResult);
    setAiNarrationGenerating(state.isGeneratingFromNarration || false);
  }, []);

  // The two scene-visual tabs report the same two flags; which one is open
  // decides whose flags the footer is looking at, so they are kept per tab.
  const [sceneVisualState, setSceneVisualState] = useState({});
  const handle3dStateChange = useCallback(
    (state) => setSceneVisualState((prev) => ({ ...prev, scene3d: state })),
    []
  );
  const handleMotionStateChange = useCallback(
    (state) => setSceneVisualState((prev) => ({ ...prev, sceneMotion: state })),
    []
  );

  const addBoxToScene = useCallback(
    // extra.motionSource: Hareketli Görsel GIF/WebP'sinin kaynak dokümanı —
    // createBox onu kutu state'ine yazar, pill'deki "AI ile düzenle" okur.
    async (url, name, extra) => {
      if (onImageSelected) {
        onImageSelected(url, name);
        onClose?.();
        return;
      }

      if (!onBoxAdded || !url) {
        return;
      }

      const IdealStudio = require("../../../core/editor/main").default;
      const { isSlide, isContainedView } = require("../../../common/utils");
      const { ID_PREFIX_BOX } = require("../../../common/constants");
      const { randomPositionGenerator } = require("../clipboard/clipboard.utils");
      const { createBox } = require("../../../common/common_tools");

      // Animate çıktısı mp4 olabilir → video kutusu (EnrichedPlayer) yarat;
      // aksi halde görsel kutusu (HotspotImages). Aksi takdirde <img src=mp4>
      // kırık zincir ikonu olarak render olur.
      const isVideoUrl = /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);
      const pluginName = isVideoUrl ? "EnrichedPlayer" : "HotspotImages";
      const plugin = IdealStudio.Plugins.get(pluginName);
      if (!plugin) {
        return;
      }

      const navItem =
        containedViewSelected && containedViews[containedViewSelected]
          ? containedViews[containedViewSelected]
          : navItems[navItemSelected];

      if (!navItem) {
        return;
      }

      const cv =
        containedViewSelected &&
        containedViews[containedViewSelected] &&
        isContainedView(containedViewSelected);
      const cvslide = cv && isSlide(navItem.type);

      const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
      const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

      // Pozisyon + id await gerektirmez → hemen üret ve spinner'ı TIKLAMA ANINDA
      // göster; kutu render olunca üstüne geçip görsel yüklenince kapanır.
      const position = {
        x: randomPositionGenerator(10, 30),
        y: randomPositionGenerator(10, 30),
        type: "absolute",
      };
      const boxId = ID_PREFIX_BOX + Date.now();
      spinForSceneAdd({ boxId, x: position.x, y: position.y, contained: cv });

      const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(
        url,
        isVideoUrl ? "video" : "image"
      );

      const initialParams = {
        parent,
        container: 0,
        name: pluginName,
        position,
        id: boxId,
        page: cv ? containedViewSelected : navItem.id || navItemSelected || navId || 0,
        url,
        width,
        height,
        widthUnit,
        heightUnit,
        fileName: name,
        ...(extra && extra.motionSource
          ? // The url too: a later "Replace File" swaps state.url, and the
            // pencil must not then edit a document that is no longer shown.
            { motionSource: { ...extra.motionSource, imageUrl: url } }
          : {}),
        // Hareketli Görsel GIF/WebP: motion kutusu gibi 0'dan kendi süresince.
        // startFrame verildiği için addBoxThunk imlece yerleştirmeyi ve boş
        // bir görsel satırına katmayı ATLAR — kutu en üstte kendi satırına
        // açılır (yeni kutu en öndedir). Verilmezse imleçte, 5 sn ve başka bir
        // görselin satırının arasında açılıyordu.
        ...(extra && Number(extra.durationSec) > 0
          ? {
              timeline: {
                visible: true,
                startFrame: 0,
                endFrame: Math.round(Number(extra.durationSec) * FPS),
              },
            }
          : {}),
      };

      createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
      recordPopupResult({ feature: "image", summary: "Added an image to the scene", url });
    },
    [
      navId,
      boxes,
      navItems,
      containedViews,
      containedViewSelected,
      boxSelected,
      navItemSelected,
      onBoxAdded,
      onImageSelected,
    ]
  );

  const handleAddToScene = useCallback(
    (url, name, extra) => {
      addBoxToScene(url, name, extra);
      onClose?.();
    },
    [addBoxToScene, onClose]
  );

  /**
   * A generated 3D object / motion graphic, approved by the author.
   *
   * Not routed through addBoxToScene: that one takes a url and guesses the
   * plugin from the file extension, which is right for an image or an mp4 and
   * wrong here — the result already carries the box and toolbar the backend
   * built, including which plugin and (for motion) the timeline length. The
   * parent applies them with the editor's own addBox, exactly as the motionize
   * result is applied.
   */
  const handleSceneVisualGenerated = useCallback(
    (result) => {
      onSceneVisualGenerated?.(result);
      onClose?.();
    },
    [onSceneVisualGenerated, onClose]
  );

  // Reset promptMode and narration when modal opens
  useEffect(() => {
    if (show) {
      setPromptMode("write");
      setNarration("");
      setModalKey((prev) => prev + 1);
    }
  }, [show]);

  useEffect(() => {
    let cancelled = false;
    if (!show || narrationText || !trainingId || !navId) return undefined;

    const loadNarration = async () => {
      try {
        const data = await SceneAudioService.getSceneAudios({ trainingId });
        if (cancelled) return;
        const scenes = data && Array.isArray(data.scenes) ? data.scenes : [];
        const scene = scenes.find((item) => item.navId === navId);
        const narrationAudio =
          scene?.audios?.find((audio) => audio.narrationText) || scene?.audios?.[0];
        setFetchedNarrationText(narrationAudio?.narrationText || "");
      } catch (error) {
        if (!cancelled) {
          setFetchedNarrationText("");
        }
      }
    };

    loadNarration();

    return () => {
      cancelled = true;
    };
  }, [show, narrationText, trainingId, navId]);

  const resolvedNarrationText = narrationText || fetchedNarrationText;

  // Whose flags the footer is reading. The AI tab keeps its own two pieces of
  // state (it also reports a third, "prompt is being written from the
  // narration", which disables the button); the scene-visual tabs report
  // theirs into one map keyed by tab.
  const activeGenerating =
    activeTab === "ai" ? aiGenerating : !!sceneVisualState[activeTab]?.isGenerating;
  const activeHasResult =
    activeTab === "ai" ? aiHasResult : !!sceneVisualState[activeTab]?.hasResult;
  const showConfirm = GENERATIVE_TABS.has(activeTab) && !activeGenerating && !activeHasResult;

  const handleConfirm = useCallback(
    () =>
      generateHandler
        ? generateHandler()
        : onGenerate?.({
            activeTab,
            aspectRatio,
            selectedStyle,
            textInImage,
            promptMode,
            narration,
            customStyleDesc: isCustomStyle ? customStyleDesc : undefined,
          }),
    [
      activeTab,
      aspectRatio,
      customStyleDesc,
      generateHandler,
      isCustomStyle,
      narration,
      onGenerate,
      promptMode,
      selectedStyle,
      textInImage,
    ]
  );

  // Custom footer rendered only when "Oluştur" button is visible (AI tab, not generating, no result).
  // Shows a credit-estimate button on the left and the standard Cancel + Generate buttons on the right.
  const customFooter = showConfirm ? (
    <>
      <style>{`@keyframes aim-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div
        className="app-modal__footer"
        style={{ justifyContent: "space-between", alignItems: "center" }}
      >
        {/* Credit estimate button + popover */}
        <div ref={creditBtnRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={handleCreditCheck}
            disabled={creditLoading}
            title={i18n.t("CreditEstimate.Tooltip")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "9px 16px",
              borderRadius: "12px",
              border: `1px solid ${creditPopoverOpen ? "#c4b5fd" : "#dce3f3"}`,
              background: creditPopoverOpen ? "#eef2ff" : "#f8fafc",
              color: creditPopoverOpen ? "#7c3aed" : "#64748b",
              fontSize: "14px",
              fontWeight: 600,
              cursor: creditLoading ? "wait" : "pointer",
              transition: "background 0.18s ease, color 0.18s ease, border-color 0.18s ease",
              outline: "none",
              whiteSpace: "nowrap",
            }}
          >
            {creditLoading ? (
              <LuRefreshCw size={14} style={{ animation: "aim-spin 0.8s linear infinite" }} />
            ) : (
              <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
            )}
            <span>
              {creditLoading ? i18n.t("CreditEstimate.Loading") : i18n.t("CreditEstimate.Button")}
            </span>
          </button>

          {creditPopoverOpen && creditInfo && (
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 10px)",
                left: 0,
                background: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                padding: "16px 20px",
                minWidth: "240px",
                boxShadow: "0 4px 24px rgba(15, 23, 42, 0.12)",
                zIndex: 9999,
                fontSize: "13px",
                lineHeight: 1.6,
              }}
            >
              {/* Arrow */}
              <div
                style={{
                  position: "absolute",
                  bottom: -7,
                  left: 20,
                  width: 13,
                  height: 13,
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderTop: "none",
                  borderLeft: "none",
                  transform: "rotate(45deg)",
                }}
              />

              <div
                style={{
                  fontWeight: 700,
                  marginBottom: 12,
                  fontSize: "14px",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
                {i18n.t("CreditEstimate.Title")}
              </div>

              {/* Per-provider cost rows */}
              {creditInfo.providers &&
                creditInfo.providers.map((p, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ color: "#64748b", fontSize: "13px" }}>{p.label}</span>
                    <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "13px" }}>
                      {p.cost.toFixed(2)}&nbsp;
                      <span style={{ color: "#8b95af", fontWeight: 400 }}>
                        {i18n.t("ImageHistory.Credits")}
                      </span>
                    </span>
                  </div>
                ))}

              {/* Total row — only when multiple providers */}
              {creditInfo.providers && creditInfo.providers.length > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 4,
                    paddingTop: 8,
                    borderTop: "1px solid #f1f5f9",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: "#64748b", fontWeight: 600 }}>
                    {i18n.t("CreditEstimate.Cost")}
                  </span>
                  <span style={{ fontWeight: 700, color: "#1e293b" }}>
                    {creditInfo.requiredCredits.toFixed(2)}&nbsp;
                    <span style={{ color: "#8b95af", fontWeight: 400 }}>
                      {i18n.t("ImageHistory.Credits")}
                    </span>
                  </span>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 14,
                  paddingBottom: 14,
                  borderBottom: "1px solid #f1f5f9",
                  marginTop: creditInfo.providers && creditInfo.providers.length === 1 ? 2 : 6,
                }}
              >
                <span style={{ color: "#64748b" }}>{i18n.t("CreditEstimate.Balance")}</span>
                <span style={{ fontWeight: 700, color: "#1e293b" }}>
                  {creditInfo.availableCredits.toFixed(2)}&nbsp;
                  <span style={{ color: "#8b95af", fontWeight: 400 }}>
                    {i18n.t("ImageHistory.Credits")}
                  </span>
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 8,
                  background: creditInfo.hasSufficientBalance
                    ? "rgba(124,58,237,0.08)"
                    : "rgba(239,68,68,0.08)",
                  color: creditInfo.hasSufficientBalance ? "#7c3aed" : "#ef4444",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {creditInfo.hasSufficientBalance
                  ? `✓ ${i18n.t("CreditEstimate.Sufficient")}`
                  : `✗ ${i18n.t("CreditEstimate.Insufficient")}`}
              </div>
            </div>
          )}
        </div>

        {/* Standard Cancel + Generate buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={() => {
              if (activeHasResult && resetAiResultsHandler) {
                resetAiResultsHandler();
                return;
              }
              (onCancel || onClose)?.();
            }}
          >
            {i18n.t("Cancel")}
          </button>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            onClick={handleConfirm}
            disabled={aiNarrationGenerating}
          >
            {/* "Görseli Oluştur" is wrong on the two tabs that produce a
                document rather than an image. */}
            {activeTab === "ai"
              ? i18n.t("GenerateImage")
              : i18n.t("SceneVisual.Generate") || "Oluştur"}
          </button>
        </div>
      </div>
    </>
  ) : undefined;

  // The two scene-visual tabs produce a box, not a url, so they are only
  // offered where the parent can apply one. Opened as a plain image picker
  // (onImageSelected), the modal has nowhere to put a MotionScene box — and a
  // tab that charges the author and then drops the result on the floor is
  // worse than a tab that is not there.
  const visibleTabs = useMemo(
    () =>
      TABS.filter((tab) =>
        SCENE_VISUAL_KIND_BY_TAB[tab.id] ? !!onSceneVisualGenerated && !onImageSelected : true
      ),
    [onSceneVisualGenerated, onImageSelected]
  );

  const titleWithTabs = (
    <div className="add-image-modal__title-with-tabs">
      <span className="add-image-modal__title-text">
        {titleLabel ? i18n.t(titleLabel) : i18n.t("AddImage") || "Add Image"}
      </span>
      <div className="add-image-modal__tabs add-image-modal__tabs--inline">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`add-image-modal__tab${activeTab === tab.id ? " add-image-modal__tab--active" : ""}`}
          >
            <span className="add-image-modal__tab-label">{i18n.t(tab.label)}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <AppModal
        show={show}
        onClose={onClose}
        title={titleWithTabs}
        className="addImageModal"
        size="lg"
        bodyProps={{ className: "add-image-modal__body" }}
        footerProps={{ className: "add-image-modal__footer" }}
        cancelLabel={i18n.t("Cancel") || "Cancel"}
        onCancel={() => {
          // Üretken sekmelerde bir sonuç görünüyorsa Vazgeç modal'ı kapatmasın;
          // yalnızca sonuçları temizleyip form'a dönsün. Böylece kullanıcının
          // yazdığı prompt, seçtiği stil/provider/aspect ratio vb. korunur.
          if (GENERATIVE_TABS.has(activeTab) && activeHasResult && resetAiResultsHandler) {
            resetAiResultsHandler();
            return;
          }
          (onCancel || onClose)?.();
        }}
        footer={customFooter}
        onConfirm={handleConfirm}
        confirmDisabled={aiNarrationGenerating}
        headerActions={
          <button
            type="button"
            onClick={() => setActiveTab(activeTab === "history" ? "ai" : "history")}
            title={i18n.t("ImageHistory.Tab") || "Etkinlik Günlüğü"}
            className={`app-modal__toggle-btn${activeTab === "history" ? " app-modal__toggle-btn--active" : ""}`}
          >
            <LuHistory size={16} />
          </button>
        }
      >
        <div className="add-image-modal">
          <div className="add-image-modal__tab-content">
            {/* AI sekmesi her zaman mount kalır, sadece aktif değilken gizlenir;
                böylece kullanıcı Activity Log gibi başka sekmeye gidip dönünce
                süren/üretilmiş görsel, prompt ve tüm seçimler korunur. */}
            <div style={{ display: activeTab === "ai" ? "block" : "none" }}>
              <AIImageGenerateTab
                key={modalKey}
                aspectRatio={aspectRatio}
                onAspectRatioChange={setAspectRatio}
                model={model}
                onModelChange={setModel}
                narrationText={resolvedNarrationText}
                onGenerateReady={handleGenerateReady}
                onResetReady={handleResetReady}
                onCreditCheckReady={handleCreditCheckReady}
                onStateChange={handleAiStateChange}
                onServerModal={onServerModal}
                trainingId={trainingId}
                sceneIndex={sceneIndex}
                navId={navId}
                onAddToScene={handleAddToScene}
                customStyleDesc={customStyleDesc}
                onCustomStyleDescChange={setCustomStyleDesc}
                onInsufficientBalance={handleInsufficientBalance}
              />
            </div>
            {/* 3B obje ve hareketli görsel. AI sekmesinin aksine aktif
                değilken mount EDİLMEZLER: üretim arka planda kuyrukta sürer ve
                sonucu iş widget'ından da onaylanabilir, o yüzden gizli bir
                kopyayı ayakta tutmanın karşılığı yok. */}
            {activeTab === "scene3d" ? (
              <SceneVisualGenerateTab
                kind={SCENE_VISUAL_KIND_BY_TAB.scene3d}
                narrationText={resolvedNarrationText}
                trainingId={trainingId}
                navId={navId}
                sceneIndex={sceneIndex}
                onGenerateReady={handle3dGenerateReady}
                onResetReady={handle3dResetReady}
                onCreditCheckReady={handle3dCreditCheckReady}
                onStateChange={handle3dStateChange}
                onServerModal={onServerModal}
                onInsufficientBalance={handleInsufficientBalance}
                onBoxGenerated={handleSceneVisualGenerated}
              />
            ) : null}
            {activeTab === "sceneMotion" ? (
              <SceneVisualGenerateTab
                kind={SCENE_VISUAL_KIND_BY_TAB.sceneMotion}
                narrationText={resolvedNarrationText}
                trainingId={trainingId}
                navId={navId}
                sceneIndex={sceneIndex}
                onGenerateReady={handleMotionGenerateReady}
                onResetReady={handleMotionResetReady}
                onCreditCheckReady={handleMotionCreditCheckReady}
                onStateChange={handleMotionStateChange}
                onServerModal={onServerModal}
                onInsufficientBalance={handleInsufficientBalance}
                onBoxGenerated={handleSceneVisualGenerated}
                // GIF / WebP çıktısı sıradan bir görsel: diğer görsel
                // sekmeleriyle aynı yoldan, HotspotImages kutusu olarak eklenir.
                onImageGenerated={(url, label, extra) =>
                  handleAddToScene(url, label || "GIF", extra)
                }
                // MP4 çıktısı: sekme video kutusunu kendisi ekler, modal kapanır.
                onMediaAdded={onClose}
              />
            ) : null}
            {activeTab === "library" ? <LibraryImageTab onAddToScene={handleAddToScene} /> : null}
            {activeTab === "upload" ? (
              <UploadImageTab uploadFunction={uploadFunction} onAddToScene={handleAddToScene} />
            ) : null}
            {activeTab === "history" ? (
              <HistoryTab mediaType="image" onAddToScene={handleAddToScene} />
            ) : null}
          </div>
        </div>
      </AppModal>

      <InsufficientBalanceModal
        show={insufficientBalance.show}
        onClose={() => setInsufficientBalance((prev) => ({ ...prev, show: false }))}
        requestedAmount={insufficientBalance.requestedAmount}
        currentBalance={insufficientBalance.currentBalance}
        kind={insufficientBalance.kind}
        teamName={insufficientBalance.teamName}
        resetsAt={insufficientBalance.resetsAt}
      />
    </>
  );
};

export default AddImageModal;
